import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Headset, MessageCircle } from 'lucide-react';
import clx from 'classnames';
import styles from './SupportContainer.module.scss';
import useWindowSize from '../../../../utils/useWindowSize';
import TitleSectionBack from '../../../Common/TitleSectionBack';

const openHubSpotChat = () => {
  const widget = window.HubSpotConversations?.widget;
  if (!widget) return false;

  const status = widget.status?.() || {};

  try {
    if (!status.loaded) {
      widget.load({ widgetOpen: true });
      return true;
    }
    widget.open();
    return true;
  } catch (error) {
    return false;
  }
};

const SupportContainer = () => {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const [isOpening, setIsOpening] = useState(false);

  useEffect(() => {
    const loadWidget = () => {
      try {
        window.HubSpotConversations?.widget?.load();
      } catch (error) {
        // Widget not ready yet
      }
    };

    if (window.HubSpotConversations?.widget) {
      loadWidget();
      return undefined;
    }

    window.hsConversationsOnReady = [
      ...(window.hsConversationsOnReady || []),
      loadWidget,
    ];

    return undefined;
  }, []);

  const handleOpenChat = useCallback(() => {
    if (openHubSpotChat()) return;

    setIsOpening(true);

    const readyHandler = () => {
      openHubSpotChat();
      setIsOpening(false);
    };

    window.hsConversationsOnReady = [
      ...(window.hsConversationsOnReady || []),
      readyHandler,
    ];

    window.setTimeout(() => {
      if (openHubSpotChat()) {
        setIsOpening(false);
      }
    }, 400);

    window.setTimeout(() => setIsOpening(false), 2500);
  }, []);

  const helpItems = [
    t('settings.supportContainer.listItems.resolveAccountIssues'),
    t('settings.supportContainer.listItems.getAnswers'),
    t('settings.supportContainer.listItems.reportBugs'),
  ];

  return (
    <div
      className={clx(styles.support_container, {
        [styles.support_container_mobile]: isMobile,
      })}
    >
      <div className={styles.header}>
        <TitleSectionBack
          title={t('settings.supportContainer.title')}
          description={t('settings.supportContainer.description')}
        />
      </div>

      <section className={styles.card}>
        <div className={styles.card_header}>
          <span className={styles.card_icon} aria-hidden="true">
            <Headset size={18} strokeWidth={2.2} />
          </span>
          <div className={styles.card_header_copy}>
            <h3 className={styles.section_title}>{t('settings.supportContainer.infoTitle')}</h3>
            <p className={styles.section_description}>
              {t('settings.supportContainer.infoTextBold')}
            </p>
          </div>
        </div>

        <div className={styles.card_body}>
          <p className={styles.section_label}>{t('settings.supportContainer.infoText')}</p>
          <ul className={styles.info_list}>
            {helpItems.map((item) => (
              <li key={item}>
                <span className={styles.list_icon} aria-hidden="true">
                  <Check size={14} strokeWidth={2.4} />
                </span>
                {item}
              </li>
            ))}
          </ul>
          <p className={styles.footer_text}>{t('settings.supportContainer.footerText')}</p>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.open_chat_button}
              onClick={handleOpenChat}
              disabled={isOpening}
            >
              <MessageCircle size={18} strokeWidth={2.2} />
              {isOpening
                ? t('settings.supportContainer.openingChat')
                : t('settings.supportContainer.openChat')}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SupportContainer;
